'use client';
import React, { useState, useEffect } from 'react';
import { ChevronDown, Shield, Clock, Star, Car } from 'lucide-react';

const floatingStats = [
{ icon: Clock, label: '24/7 Service', key: 'stat-247' },
{ icon: Shield, label: 'Verified Drivers', key: 'stat-verified' },
{ icon: Star, label: 'Safe Rides', key: 'stat-safe' },
{ icon: Car, label: 'Luxury Cars', key: 'stat-luxury' }];


export default function HeroSection() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const scrollToFleet = () => {
    const el = document.getElementById('fleet');
    if (el) el?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1702306258625-acc1ac6df8fe"
          alt="Premium luxury cab driving on Delhi highway at night with city lights"
          className="w-full h-full object-cover object-center" />
        
        <div className="hero-overlay absolute inset-0" />
        {/* Blue glow accent */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/10 blur-[100px] rounded-full" />
      </div>
      {/* Content */}
      <div
        className={`relative z-10 text-center px-6 max-w-4xl mx-auto transition-all duration-1000 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
        }>
        
        {/* Badge */}
        <div className="inline-flex items-center gap-2 glass-card px-4 py-2 rounded-full mb-6 border border-primary/20">
          <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          <span className="text-xs font-semibold text-primary tracking-widest uppercase">
            Premium Cab Service — Delhi
          </span>
        </div>

        {/* Main Heading */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-4">
          Reliable Taxi Service
          <br />
          <span className="gradient-text">in Delhi</span>
        </h1>

        {/* Subtitle */}
        <p className="text-xl sm:text-2xl font-semibold text-primary mb-3 tracking-wide">
          Safe • Comfortable • Affordable
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
          <button
            onClick={scrollToFleet}
            className="btn-primary px-8 py-4 rounded-xl text-lg font-bold w-full sm:w-auto glow-blue-sm animate-pulse-glow">
            
            🚖 Book Cab Now
          </button>
          <a
            href="https://wa.me/918860044910?text=Hi%20Navya%20Travels%2C%20I%20want%20to%20book%20a%20cab%20from%20Delhi"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost px-8 py-4 rounded-xl text-lg font-bold w-full sm:w-auto flex items-center justify-center gap-2">
            
            <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            WhatsApp Booking
          </a>
        </div>

        {/* Floating Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto">
          {floatingStats?.map((stat, i) =>
          <div
            key={stat?.key}
            className="stat-card px-3 py-3 flex flex-col items-center gap-1 animate-float"
            style={{ animationDelay: `${i * 0.4}s` }}>
            
              <stat.icon size={20} className="text-primary" />
              <span className="text-xs font-semibold text-white text-center">{stat?.label}</span>
            </div>
          )}
        </div>
      </div>
      {/* Scroll Indicator */}
      <button
        onClick={scrollToFleet}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-muted-foreground hover:text-primary transition-colors animate-bounce"
        aria-label="Scroll to fleet">
        
        <ChevronDown size={28} />
      </button>
    </section>
  );

}